import React, { useEffect, useState } from 'react';
import './PhoneNumberSettings.scss';
import ForwardCalls from '../Steps/ForwardCalls';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	getProvisionedNumber,
	getTwilioBundleStatus,
	resetTwilioBundle,
} from 'src/api/reservedNumbers';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgent, getSelectedAgentId } from '@/store/account/selector';
import { getUser, updatePendingTestingStatus, updateStepsCompleted } from '@/api/user';
import { setInstructionSteps, updateSelectedAgent } from '@/store/account/slice';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { Button, Alert, notification } from 'antd';
import Paragraph from 'antd/es/typography/Paragraph';
import { CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { setPhoneNumber } from '@/store/phoneNumber/slice';

interface TestItOutProps {
	setOpened?: (open: boolean) => void;
	setOpenedOnboarding?: (open: boolean) => void;
	debrand: boolean;
	renderCentralComponent?: boolean;
	setExtProvisionedNumber?: any;
}

export const PhoneNumberSettings: React.FC<TestItOutProps> = ({
	debrand,
	setOpenedOnboarding,
	renderCentralComponent,
	setExtProvisionedNumber,
}) => {
	const [provisionedNumber, setProvisionedNumber] = useState<string | null>(null);
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const selectedAgent = useAppSelector(getSelectedAgent);
	const dispatch = useAppDispatch();

	const UpdateStepsDoneMutation = useMutation({
		mutationKey: ['updateStepsDone'],
		mutationFn: updateStepsCompleted,
		onSuccess(data: { success: boolean; updated_instruction_steps: number[] }) {
			dispatch(setInstructionSteps({ instructionSteps: data.updated_instruction_steps }));
		},
	});

	const UpdatePendingTestingStatusMutation = useMutation({
		mutationKey: ['updatePendingTestingStatus'],
		mutationFn: updatePendingTestingStatus,
	});
	const handleSetPendingTesting = (status: boolean) => {
		UpdatePendingTestingStatusMutation.mutate({
			pendingTesting: status,
			agentId: selectedAgentId as string,
		});
	};

	const updateStepsDone = (stepNumber: number) => {
		UpdateStepsDoneMutation.mutate({ stepNumber, agentId: selectedAgentId as string });
	};

	const getProvisionNumberMutation = useMutation({
		mutationKey: ['provisionedNumberApi'],
		mutationFn: () => getProvisionedNumber(selectedAgentId as string),
		onSuccess(data) {
			setProvisionedNumber(data.provisioned_number);
			setExtProvisionedNumber(data.provisioned_number);
			dispatch(setPhoneNumber({ phoneNumber: data.provisioned_number || '' }));
		},
	});
	const { refetch } = useQuery({
		queryKey: ['receptionist'],
		queryFn: () => getUser(selectedAgentId as string),
		enabled: !!selectedAgentId,
	});

	const getBundleIdMutation = useMutation({
		mutationKey: ['getBundleId'],
		mutationFn: getTwilioBundleStatus,
		onSuccess(data) {
			refetch();

			dispatch(
				updateSelectedAgent({
					user: {
						twilioBundleId: data.sid,
						twilioBundleStatus: data.status,
						twilioBundleFailureReason: data.failureReasons,
					},
				}),
			);

			// if this if triggers that means it has came from approve bundle modal
			if (!selectedAgent?.twilioBundleId) {
				dispatch(
					updateSelectedAgent({
						user: { twilioBundleId: data.sid, twilioBundleStatus: 'pending-review' },
					}),
				);
			}
		},
	});

	const { mutate: resetBundleMutation } = useMutation({
		mutationKey: ['resetBundle'],
		mutationFn: resetTwilioBundle,
		onSuccess: () => {
			dispatch(updateSelectedAgent({ user: { twilioBundleId: null, twilioBundleStatus: null } }));
			notification.success({ message: 'Bundle reset successfully' });
		},
		onError: () => {
			notification.error({ message: 'Failed to reset bundle' });
		},
	});

	useEffect(() => {
		getProvisionNumberMutation.mutate();
		if (selectedAgent?.twilioBundleId) {
			getBundleIdMutation.mutate(selectedAgent?.twilioBundleId);
		}
	}, []);

	const baseClass = 'test-it-out-container';

	if (renderCentralComponent) {
		return (
			<div>
				{selectedAgent?.twilioBundleId &&
					selectedAgent?.twilioBundleStatus === 'twilio-approved' && (
						<>
							<Paragraph type="success">
								<CheckCircleOutlined /> Regulatory process successfully completed. You can now get
								your standalone number.
							</Paragraph>
						</>
					)}
				{selectedAgent?.twilioBundleId &&
					selectedAgent?.twilioBundleStatus === 'twilio-rejected' && (
						<>
							<Paragraph>Your regulatory process failed due to the following reasons:</Paragraph>
							<Paragraph type="danger">{selectedAgent?.twilioBundleFailureReason}</Paragraph>
							<Button onClick={() => resetBundleMutation(selectedAgent?.number)}>
								Restart regulatory process
							</Button>
						</>
					)}
				{(selectedAgent?.twilioBundleId && selectedAgent?.twilioBundleStatus === 'in-review') ||
				selectedAgent?.twilioBundleStatus === 'pending-review' ? (
					<Paragraph>
						<LoadingOutlined /> Your information is under review. It can take up to 2 to 3 business
						days to get it approved.
					</Paragraph>
				) : (
					<ForwardCalls
						debrand={debrand}
						provisionedNumber={provisionedNumber}
						setProvisionedNumber={setProvisionedNumber}
						getProvisionNumberMutation={getProvisionNumberMutation}
						getBundleIdMutation={getBundleIdMutation}
						setOpenedOnboarding={setOpenedOnboarding}
						renderCentralComponent={renderCentralComponent}
					/>
				)}
			</div>
		);
	}
	return (
		<div>
			<div className={`${baseClass} flex flex-col`}>
				<div className={`${baseClass}__title`}>
					<h2 className="text-2xl font-bold">Your AI Phone Number</h2>
					<span>Manage your AI Phone Number here.</span>
				</div>
				<Alert
					className="overview-info"
					closable
					description="Get your AI Phone Number set up in minutes! 👇"
				/>
				<ForwardCalls
					debrand={debrand}
					provisionedNumber={provisionedNumber}
					setProvisionedNumber={setProvisionedNumber}
					getProvisionNumberMutation={getProvisionNumberMutation}
					getBundleIdMutation={getBundleIdMutation}
					setOpenedOnboarding={setOpenedOnboarding}
				/>

				{provisionedNumber && !selectedAgent?.instruction_steps?.includes(6) && (
					<Button
						type="primary"
						className="mt-4 max-w-20"
						onClick={() => {
							updateStepsDone(6);
							handleSetPendingTesting(false);
						}}
					>
						Done
					</Button>
				)}
			</div>
		</div>
	);
};
